/**
 * Single entry point for talking to the Vellby backend.
 *
 * The backend takes the acting user from the bearer token (backend auth.py), so
 * there is no need to pass user_id as a query param or body field for identity;
 * where a `user_id` param still appears it is a filter, not "who am I".
 *
 * Every component used to repeat the same three decisions inline: where the API
 * lives (REACT_APP_API_URL), how to attach the Keycloak bearer token, and how to
 * read an error off a failed response. That knowledge is now owned here and
 * nowhere else, so changing any of it (auth scheme, error shape, base URL) is a
 * one-file change instead of a 100-site sweep.
 *
 * Token freshness is handled for the caller: each request first asks Keycloak to
 * refresh the token if it is close to expiry. This removes the whole class of
 * "request failed because the token went stale" bugs — callers never see it.
 *
 * Usage:
 *   import api from '../utils/api_client';
 *   const people = await api.get('/api/entities', { params: { entity_type: 'person', user_id } });
 *   const created = await api.post('/api/connections', { entity_a_id, entity_b_id });
 *   await api.del(`/api/connections/${id}`, { invalidate: '/api/entities' });
 *
 * On failure every method throws an ApiError (below) — one type, always carrying
 * .status and .detail — so callers handle errors the same way everywhere.
 */

import keycloak from '../keycloak';
import getEnv from '../env';
import { cachedFetch, invalidateCachePrefix } from './api_cache';
import { readGuest } from './guest_session';

const baseUrl = () => getEnv('REACT_APP_API_URL') || 'http://localhost:8000';

// Refresh the token when it has fewer than this many seconds left.
const TOKEN_MIN_VALIDITY_SECONDS = 30;

/**
 * The single error type for every backend call. FastAPI returns `{ detail: ... }`
 * on errors; `detail` holds that payload (object or string), `status` the HTTP
 * code. `message` is a human-readable string suitable for message.error().
 */
export class ApiError extends Error {
    constructor(status, detail, url) {
        const text =
            typeof detail === 'string' ? detail :
            detail && typeof detail.detail === 'string' ? detail.detail :
            `Request failed (${status})`;
        super(text);
        this.name = 'ApiError';
        this.status = status;
        this.detail = detail;
        this.url = url;
    }
}

function buildUrl(path, params) {
    // Absolute URLs pass through untouched; everything else is relative to the API root.
    const url = new URL(path.startsWith('http') ? path : `${baseUrl()}${path}`);
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === null) return; // omit empties rather than sending "undefined"
            if (Array.isArray(value)) value.forEach(v => url.searchParams.append(key, v));
            else url.searchParams.append(key, value);
        });
    }
    return url.toString();
}

async function authHeaders() {
    // Best-effort refresh. If Keycloak can't refresh (e.g. not yet initialised),
    // we still send whatever token we have and let the server decide.
    try {
        await keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
    } catch {
        /* fall through with the current token */
    }
    if (keycloak.token) return { Authorization: `Bearer ${keycloak.token}` };
    // A Gathering guest has no Keycloak session; their guest id (from
    // POST /api/gathering/guest/join) is their bearer credential. The backend
    // (auth.py) only honours it on a profile that is a current guest.
    const guest = readGuest();
    if (guest?.user_id) return { Authorization: `Bearer ${guest.user_id}` };
    return {};
}

async function parseResponse(res, url) {
    if (!res.ok) {
        // Prefer the JSON error body (FastAPI's { detail }); fall back to text.
        let detail = null;
        try {
            detail = await res.json();
        } catch {
            try { detail = await res.text(); } catch { /* no body */ }
        }
        throw new ApiError(res.status, detail, url);
    }
    if (res.status === 204) return null;
    // Read as text first so an empty 200 body becomes null instead of a parse error.
    const text = await res.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch { return text; }
}

/**
 * @param {string} method  HTTP verb
 * @param {string} path    e.g. '/api/entities' (or an absolute URL)
 * @param {object} [opts]
 * @param {object} [opts.params]     query params; arrays become repeated keys, nullish dropped
 * @param {*}      [opts.body]       JSON-serialisable body (sets Content-Type automatically)
 * @param {boolean}[opts.cache]      GET only: use the shared 30s TTL + in-flight dedup cache
 * @param {string} [opts.invalidate] after a mutation, evict cached GETs whose URL starts with this
 * @param {AbortSignal} [opts.signal]
 * @param {object} [opts.headers]    extra headers (rarely needed)
 */
async function request(method, path, opts = {}) {
    const { params, body, cache, invalidate, signal, headers } = opts;
    const url = buildUrl(path, params);
    const hasBody = body !== undefined;
    // FormData (file uploads) must keep its own multipart boundary — the
    // browser sets Content-Type itself; JSON.stringify would mangle the body.
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const fetchOpts = {
        method,
        signal,
        headers: {
            ...(hasBody && !isFormData && { 'Content-Type': 'application/json' }),
            ...(await authHeaders()),
            ...headers,
        },
        ...(hasBody && { body: isFormData ? body : JSON.stringify(body) }),
    };

    // Reuse the existing cache layer for opt-in GETs. It throws a plain Error, so
    // normalise that into an ApiError here to keep a single error type everywhere.
    if (method === 'GET' && cache) {
        try {
            return await cachedFetch(url, fetchOpts);
        } catch (err) {
            const status = Number((String(err.message).match(/\d{3}/) || [])[0]) || 0;
            throw new ApiError(status, err.message, url);
        }
    }

    const result = await parseResponse(await fetch(url, fetchOpts), url);

    // Mutations make cached reads stale; let callers evict the affected prefix.
    if (invalidate) invalidateCachePrefix(buildUrl(invalidate));

    return result;
}

const api = {
    get: (path, opts) => request('GET', path, opts),
    post: (path, body, opts) => request('POST', path, { ...opts, body }),
    put: (path, body, opts) => request('PUT', path, { ...opts, body }),
    patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
    del: (path, opts) => request('DELETE', path, opts),
    ApiError,
};

export default api;
